/* eslint-disable max-len */
import { CloseBtn, DropdownDiv } from './style';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { setDropdownState } from 'store/slices/EditFacetModalSlice';
import { Popup } from 'reactjs-popup';
import { setEditShadingPerfomed, setUnfinalizedFacet } from 'store/slices/ToolSlice';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import Button from 'components/Basic/Button';
import { useRef } from 'react';
import { useRefs } from 'contexts/RefContext';
import { getExistingPanelsGroupID } from 'components/tool/utils';
import { CloseIcon } from 'assets/svg';
import { useMutation } from 'react-query';
import { adjustShadingInit, undoRemoveShading } from '../ButtonDiv/api';
import { openShadingMenu } from 'store/slices/AdjustShadingSlice';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { AxiosError } from 'axios';
import { appToast } from 'components/Basic/CustomToast';
import { getDeratedValue, isModeDefault, isInHouseSimulationMode } from '../../../../util';
import { EnergyState } from 'store/slices/EnergySlice/types';
import { getRoofOptions, getSolarOptions, Option } from './options';

export default function EditFacetModal() {
	const { position, open } = useSelector((state: RootState) => state.editFacetModal.data);
	const { drawModeEnabled, selectedRoofIndex, roofIndexes, hadTransition } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { allRoofSegs, mode, designType, uuid, panel } = useSelector((state: RootState) => state.roofData.data);
	const allowedActions = useSelector((state: RootState) => state.roleState.data.allowedActions);
	const { selectedPanels: selectedPanelsOnUI = [], deratedBy } = useSelector(
		(state: RootState) => (state.energyData.data ?? {}) as EnergyState
	);
	const dispatch = useDispatch<AppDispatch>();
	const popupRef = useRef<HTMLDivElement | null>(null);
	const { konvaRef } = useRefs();
	const queriesRunningInBackGround = useQueriesRunningInBackGround();

	const facet = allRoofSegs.find(rs => rs.id === selectedRoofIndex);

	// --- derived visibility flags for the solar option list ---
	// hide remove shading / change orientation when the facet has no panels
	const hasSelectedFacetPanels = !!selectedRoofIndex &&
		!!konvaRef?.current?.findOne(`#${getExistingPanelsGroupID(selectedRoofIndex)}`)?.hasChildren();
	const facetIsTransformed = roofIndexes.length > 0;
	const isDefaultMode = isModeDefault(mode);
	const isGroundMount = !!facet?.isGroundMount;
	const inHouseSimulationMode = isInHouseSimulationMode(mode);

	const showChangeOrientationOption = hasSelectedFacetPanels && !facetIsTransformed;
	const showRemoveShadingOption = isDefaultMode && !isGroundMount && hasSelectedFacetPanels && !inHouseSimulationMode;
	const showEditPitchAndShadingOption = hasSelectedFacetPanels && (!isDefaultMode || (!inHouseSimulationMode && isGroundMount));
	const showEditPitchOption = hasSelectedFacetPanels && isDefaultMode && (inHouseSimulationMode || (!inHouseSimulationMode && !isGroundMount));

	// --- shading production/size totals used by the adjust-shading menu payload ---
	const { totalSelectedPanelsFromFacet, totalSelectedPanelsProduction } = (facet?.panels ?? []).reduce((sum, panel) => ({
		totalSelectedPanelsFromFacet: sum.totalSelectedPanelsFromFacet + (selectedPanelsOnUI.includes(panel.id) ? 1 : 0),
		totalSelectedPanelsProduction: sum.totalSelectedPanelsProduction + (selectedPanelsOnUI.includes(panel.id) ? panel.yearlyEnergyDcKwh : 0)
	}), { totalSelectedPanelsFromFacet: 0, totalSelectedPanelsProduction: 0 });
	const { totalPanelsFromFacet, totalPanelsProduction } = (facet?.panels ?? []).reduce((sum, panel) => ({
		totalPanelsFromFacet: sum.totalPanelsFromFacet + 1,
		totalPanelsProduction: sum.totalPanelsProduction + panel.yearlyEnergyDcKwh
	}), { totalPanelsFromFacet: 0, totalPanelsProduction: 0 });
	const azimuth = facet?.azimuthDegrees || 0;
	const pitch = facet?.pitchDegrees || 0;

	const isPatched = !!facet?.isFluxPatched;
	const shadingActionType = isPatched ? (facet?.isShadingAdjusted ? 'adjust' : 'undo') : (facet?.isShadingAdjusted ? 'adjust' : 'init');

	const removeShadingQuery = useMutation(async () => {
		if (!facet) return;
		const payload = {
			facetId: facet.id as string,
			panelKey: panel.key,
			isUserMapped: !!facet.userMapped,
			shading: Math.round(facet?.shading || 0),
		};
		const request$ = shadingActionType === 'undo' ? undoRemoveShading : adjustShadingInit;
		if (shadingActionType === 'adjust') {
			dispatch(openShadingMenu(
				{
					azimuth: azimuth,
					pitch: pitch,
					systemProduction: getDeratedValue(totalSelectedPanelsProduction, deratedBy),
					panelsCount: totalSelectedPanelsFromFacet,
					systemSize: totalSelectedPanelsFromFacet * panel.wattage,
					segmentId: facet.id,
					shadingVal: facet?.shading ?? 0,
					isUserMapped: !!facet?.userMapped,
					panelKey: panel.key,
					isShadingAdjusted: !!facet?.isShadingAdjusted,
					doneEnabled: false,
					totalPanelsProduction: getDeratedValue(totalPanelsProduction, deratedBy),
					totalSystemSize: totalPanelsFromFacet * panel.wattage,
					totalPanelsFromFacet: totalPanelsFromFacet,
				}
			));
			return;
		}
		const reponse = await request$(uuid, payload);
		dispatch(setEditShadingPerfomed({ editShadingPerformed: true }));
		await dispatch(changePanel({ uuid, panelKey: panel.key, designType: designType }));
		dispatch(setEditShadingPerfomed({ editShadingPerformed: false }));
		return { shading: reponse.data.shading, isUserMapped: !!facet?.userMapped, facetId: facet.id };
	},
	{
		mutationKey: 'remove-shading',
		onSuccess(reponse) {
			if (!reponse) return;
			const { shading, isUserMapped, facetId } = reponse;
			if (shadingActionType === 'init') {
				dispatch(openShadingMenu({
					azimuth: azimuth,
					pitch: pitch,
					systemProduction: getDeratedValue(totalSelectedPanelsProduction, deratedBy),
					panelsCount: totalSelectedPanelsFromFacet,
					systemSize: totalSelectedPanelsFromFacet * panel.wattage,
					segmentId: facetId,
					shadingVal: shading,
					isUserMapped: isUserMapped,
					panelKey: panel.key,
					isShadingAdjusted: !!facet?.isShadingAdjusted,
					doneEnabled: true,
					totalPanelsProduction: getDeratedValue(totalPanelsProduction, deratedBy),
					totalSystemSize: totalPanelsFromFacet * panel.wattage,
					totalPanelsFromFacet: totalPanelsFromFacet,
				}));
			}
		},
		onError(error: AxiosError<{ message: string }>) {
			if (error.response?.data) {
				appToast.error(error.response.data?.message || error.message);
			}
		}
	});

	const solarOptions = getSolarOptions({
		drawModeEnabled,
		hadTransition,
		showChangeOrientationOption,
		showRemoveShadingOption,
		showEditPitchAndShadingOption,
		showEditPitchOption,
		isFluxPatched: isPatched,
		shadingActionType,
		triggerRemoveShading: () => {
			removeShadingQuery.mutate();
			return { type: 'REMOVE_SHADING_MUTATION_TRIGGERED' };
		},
	});
	const roofOptions = getRoofOptions({ drawModeEnabled });

	function handleOptionClick(option: Option) {
		closeDropdwon();
		dispatch(option.action());
	}

	function closeDropdwon() {
		dispatch(setDropdownState({ open: false, position, pointIndex: null, segmentId: null }));
	}

	const overlayStyle = { background: 'rgba(0,0,0,0)' };

	const Options = (designType === 'ROOF' ? roofOptions : solarOptions).filter(option => allowedActions.includes(option.actionId));
	if (!Options.length) {
		return <></>;
	}
	return (
		<Popup
			contentStyle={{
				width: 'fit-content',
				borderRadius: '12px',
				left: position?.x,
				top: position?.y,
				position: 'fixed',
				background: 'transparent',
				border: 'none',
				padding: 0,
				boxShadow: 'none',
			}}
			open={open}
			closeOnDocumentClick={true}
			onClose={closeDropdwon}
			{...{ overlayStyle }}
		>
			<DropdownDiv ref={popupRef}>
				<CloseBtn style={{ marginBottom: '10px' }}>
					<CloseIcon
						style={{ color: '#fff' }}
						onClick={() => {
							dispatch(setUnfinalizedFacet({ id: null }));
							closeDropdwon();
						}}
					/>
				</CloseBtn>
				{Options.map((option) => (
					option.show && <Button
						className={`menu-item ${option.label === 'Delete Facet' ? 'danger' : ''}`}
						key={option.label}
						disabled={queriesRunningInBackGround}
						onClick={() => handleOptionClick(option)}
					>
						{option.icon}
						<span>{option.label}</span>
					</Button>
				))}
			</DropdownDiv>
		</Popup>
	);
}
